import React, { useEffect, useState, useRef } from 'react';
import { ArrowUp, ChevronLeft, ChevronRight, Users, MapPin, X, Loader, Edit3, Star, StarHalf } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const getImageSrc = (url) => {
  if (!url) return null;
  let match = url.match(/(?:file\/d\/|open\?id=|uc\?id=)([\w-]+)/);
  if (!match) match = url.match(/[?&]id=([\w-]+)/);
  let directUrl = url;
  if (match?.[1]) directUrl = `https://drive.google.com/uc?export=view&id=${match[1]}`;
  return `https://api.musicalmeet.com/api/proxy-image?url=${encodeURIComponent(directUrl)}`;
};


const FeaturedBands = () => {
  const [bands, setBands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArrow, setShowArrow] = useState(false);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [locationPermission, setLocationPermission] = useState('prompt');
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [manualCity, setManualCity] = useState('');
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [searchRadius, setSearchRadius] = useState(2000);
  const [hasTriedAutoLocation, setHasTriedAutoLocation] = useState(false);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  // Fetch bands (mirrors artist logic; update endpoints if backend differs)
  const fetchBands = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const loggedInBandId = localStorage.getItem('band_id');
      let url = 'https://api.musicalmeet.com/api/filters/nearby?'; // Adjust if bands have a different nearby endpoint
      const params = new URLSearchParams();

      if (userLocation?.isManual) {
        params.append('city', userLocation.city);
      } else if (userLocation?.latitude) {
        params.append('latitude', userLocation.latitude);
        params.append('longitude', userLocation.longitude);
        params.append('radius', searchRadius);
      }

      if (params.toString() === '') {
        // Fallback: fetch all bands (endpoint assumed; change if different)
        const res = await fetch('https://api.musicalmeet.com/api/bands');
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to fetch bands');

        const filteredData = loggedInBandId && /^[0-9a-fA-F]{24}$/.test(loggedInBandId)
          ? data.filter(b => b._id !== loggedInBandId)
          : data;
        setBands(filteredData);
        return;
      }

      console.log('Making band request to:', `${url}${params.toString()}`);
      const res = await fetch(`${url}${params.toString()}`);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Backend band error:', { status: res.status, statusText: res.statusText, errorText });
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      const filteredData = loggedInBandId && /^[0-9a-fA-F]{24}$/.test(loggedInBandId)
        ? data.filter(b => b._id !== loggedInBandId)
        : data;
      setBands(Array.isArray(filteredData) ? filteredData : []);
    } catch (err) {
      console.error('Band fetch error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const requestLocationPermission = async (isAutoRequest = false) => {
    if (!navigator.geolocation) {
      if (!isAutoRequest) {
        setLocationError('Geolocation not supported');
        setShowLocationDialog(true);
      } else {
        fetchBands();
      }
      return;
    }

    setLocationError(null);
    setIsRequestingLocation(true);

    let permissionStatus = null;
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        permissionStatus = permission.state;
      } catch (_) { /* ignore */ }
    }

    if (permissionStatus === 'denied' && !isAutoRequest) {
      setLocationPermission('denied');
      setLocationError('Location blocked. Enable in site settings or enter city manually.');
      setShowLocationDialog(true);
      setIsRequestingLocation(false);
      return;
    }

    const tryGetPosition = (options, label) => new Promise((resolve, reject) => {
      let done = false;
      const timer = setTimeout(() => { if (!done) { done = true; reject(new Error('CUSTOM_TIMEOUT')); } }, options._customTimeout || 12000);
      navigator.geolocation.getCurrentPosition(
        pos => { if (done) return; done = true; clearTimeout(timer); resolve({ pos, label }); },
        err => { if (done) return; done = true; clearTimeout(timer); reject(err); },
        options
      );
    });

    try {
      let result;
      try {
        result = await tryGetPosition({ enableHighAccuracy: false, maximumAge: 300000, timeout: 15000, _customTimeout: 8000 }, 'coarse');
      } catch (e1) {
        try {
          result = await tryGetPosition({ enableHighAccuracy: true, maximumAge: 0, timeout: 30000, _customTimeout: 20000 }, 'high');
        } catch (e2) {
          try {
            const ipRes = await fetch('https://ipapi.co/json/');
            if (ipRes.ok) {
              const ipData = await ipRes.json();
              if (ipData?.city) {
                const loc = { city: ipData.city, state: ipData.region, isManual: true, ipFallback: true };
                setUserLocation(loc);
                localStorage.setItem('band_location', JSON.stringify(loc));
                setLocationPermission('approximate');
                fetchBands();
                setIsRequestingLocation(false);
                if (!isAutoRequest) setLocationError('Using approximate location. Allow precise for better results.');
                return;
              }
            }
          } catch (_) {}
          if (!isAutoRequest) {
            setLocationError('Could not get location. Enter city manually.');
            setShowLocationDialog(true);
          } else {
            fetchBands();
          }
          setIsRequestingLocation(false);
          return;
        }
      }

      if (!result) { fetchBands(); setIsRequestingLocation(false); return; }

      const { latitude, longitude } = result.pos.coords;
      const loc = { latitude, longitude, city: 'Your Location', isManual: false, accuracyMode: result.label };
      setUserLocation(loc);
      setLocationPermission('granted');
      localStorage.setItem('band_location', JSON.stringify(loc));

      (async () => {
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`);
          const d = await r.json();
          if (d.address) {
            loc.city = d.address.city || d.address.town || d.address.village || d.address.county || loc.city;
            if (d.address.state) loc.state = d.address.state;
            setUserLocation({ ...loc });
            localStorage.setItem('band_location', JSON.stringify(loc));
          }
        } catch (e) { console.warn('Band geocode failed', e); }
      })();
    } finally {
      setIsRequestingLocation(false);
      if (isAutoRequest) setHasTriedAutoLocation(true);
    }
  };

  const handleManualLocation = () => {
    const city = manualCity.trim();
    if (city.length < 2) { setError('Please enter a valid city name'); return; }
    const loc = { city, isManual: true };
    setUserLocation(loc);
    setShowLocationDialog(false);
    setLocationPermission('manual');
    localStorage.setItem('band_location', JSON.stringify(loc));
    setManualCity('');
    setError(null);
  };

  const resetLocation = () => {
    setUserLocation(null);
    setLocationPermission('prompt');
    setHasTriedAutoLocation(false);
    localStorage.removeItem('band_location');
  };

  const scrollLeft = () => { scrollContainerRef.current?.scrollBy({ left: -300, behavior: 'smooth' }); };
  const scrollRight = () => { scrollContainerRef.current?.scrollBy({ left: 300, behavior: 'smooth' }); };

  useEffect(() => {
    const saved = localStorage.getItem('band_location');
    if (saved) { setUserLocation(JSON.parse(saved)); setHasTriedAutoLocation(true); }
  }, []);

  useEffect(() => { fetchBands(); }, [userLocation, searchRadius]);

  useEffect(() => {
    const onScroll = () => setShowArrow(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    let t;
    const handler = () => { setShowScrollButtons(true); clearTimeout(t); t = setTimeout(() => setShowScrollButtons(false), 3000); };
    container.addEventListener('scroll', handler);
    return () => { container.removeEventListener('scroll', handler); clearTimeout(t); };
  }, []);

  return (
    <section style={{ background: '#0f172a', padding: '56px 0 40px', overflowX: 'hidden' }}>

      {/* ── Section Header ── */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px 24px', textAlign: 'center' }}>

        <h2 style={{ fontFamily: 'Gilroy-semiBold', fontWeight: 700, fontSize: 'clamp(1.3rem, 3.5vw, 2rem)', color: '#fff', margin: 0, lineHeight: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 8 }}>
          {userLocation ? (
            <>
              Bands near{' '}
              <span style={{ color: '#818cf8' }}>
                {userLocation.city}{userLocation.state ? `, ${userLocation.state.slice(0, 2).toUpperCase()}` : ''}
              </span>
              <button onClick={() => setShowLocationDialog(true)} title="Change location"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#818cf8', padding: '2px 4px', display: 'inline-flex', alignItems: 'center' }}>
                <Edit3 size={17} />
              </button>
            </>
          ) : 'Featured Bands'}
        </h2>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(6px, 2vw, 10px)', marginTop: 12, flexWrap: 'wrap' }}>
          {!userLocation ? (
            <>
              <button onClick={() => requestLocationPermission(false)} disabled={isRequestingLocation}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(129,140,248,0.12)', border: '1px solid rgba(129,140,248,0.3)', color: '#818cf8', borderRadius: 8, padding: 'clamp(6px, 2vw, 10px) clamp(10px, 3vw, 14px)', fontFamily: 'Gilroy-semiBold', fontSize: 'clamp(0.7rem, 2.5vw, 0.82rem)', cursor: 'pointer', transition: 'background 0.2s', whiteSpace: 'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(129,140,248,0.22)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(129,140,248,0.12)'}>
                {isRequestingLocation ? <><Loader size={13} className="animate-spin" /> Locating…</> : <><MapPin size={13} /> Use my location</>}
              </button>
              <button onClick={() => setShowLocationDialog(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#94a3b8', borderRadius: 8, padding: 'clamp(6px, 2vw, 10px) clamp(10px, 3vw, 14px)', fontFamily: 'Gilroy-semiBold', fontSize: 'clamp(0.7rem, 2.5vw, 0.82rem)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Enter city
              </button>
            </>
          ) : (
            <>
              {!userLocation.isManual && (
                <select value={searchRadius} onChange={e => setSearchRadius(Number(e.target.value))}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', borderRadius: 8, padding: 'clamp(5px, 1.5vw, 10px)', fontFamily: 'Poppins, sans-serif', fontSize: 'clamp(0.65rem, 2vw, 0.78rem)', cursor: 'pointer' }}>
                  <option value={10}>10 km</option>
                  <option value={25}>25 km</option>
                  <option value={50}>50 km</option>
                  <option value={2000}>All</option>
                </select>
              )}
              <button onClick={resetLocation}
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: 'clamp(0.65rem, 2vw, 0.78rem)', textDecoration: 'underline', padding: 0 }}>
                Clear location
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '56px 0' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-400" />
        </div>
      )}

      {/* ── Error ── */}
      {locationError && !isLoading && (
        <div style={{ maxWidth: 400, margin: '0 auto 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '10px 16px', color: '#fca5a5', fontFamily: 'Poppins, sans-serif', fontSize: '0.85rem', textAlign: 'center' }}>
          {locationError}
        </div>
      )}

      {/* ── Empty ── */}
      {!isLoading && bands.length === 0 && (
        <div style={{ maxWidth: 400, margin: '0 auto 16px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, padding: '10px 16px', color: '#a5b4fc', fontFamily: 'Poppins, sans-serif', fontSize: '0.85rem', textAlign: 'center' }}>
          No bands found{userLocation ? ` near ${userLocation.city}` : ''}. Try adjusting your search.
        </div>
      )}

      {/* ── Carousel ── */}
      {bands.length > 0 && (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ position: 'relative', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden' }} className="group">

            <button onClick={scrollLeft}
              className={`absolute left-3 top-1/2 -translate-y-1/2 z-20 rounded-full w-9 h-9 flex items-center justify-center transition-all duration-300 ${showScrollButtons ? 'opacity-100' : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'}`}
              style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer' }}>
              <ChevronLeft size={18} />
            </button>

            <div ref={scrollContainerRef} className="scrollbar-hide"
              style={{ display: 'flex', overflowX: 'auto', gap: 16, padding: '16px 12px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', justifyContent: 'safe center' }}>
              {bands.map((band) => {
                const imgSrc = getImageSrc(band.imageUrl);
                const genres = Array.isArray(band.genres)
                  ? band.genres.map(g => typeof g === 'string' ? g : g?.name).filter(Boolean).slice(0, 2) : [];
                const instruments = Array.isArray(band.instruments)
                  ? band.instruments.map(i => i?.name || i).filter(Boolean).slice(0, 2) : [];
                const tagsLine = [...genres, ...instruments].join(' · ') || null;
                const fullStars = Math.floor(Number(band.rating) || 0);
                const hasHalf = (Number(band.rating) || 0) % 1 >= 0.3;

                return (
                  <div key={band._id}
                    onClick={() => navigate(`/public-band/${band._id}`)}
                    style={{ flexShrink: 0, width: 224, scrollSnapAlign: 'start', cursor: 'pointer', borderRadius: 14, overflow: 'hidden', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', transition: 'transform 0.22s ease, box-shadow 0.22s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 14px 36px rgba(99,102,241,0.22)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>

                    {/* Image */}
                    <div style={{ position: 'relative', height: 168, overflow: 'hidden', background: 'linear-gradient(135deg,#1E1B4B,#4338CA)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {imgSrc ? (
                        <img src={imgSrc} alt={band.name} crossOrigin="anonymous"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
                          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.06)'}
                          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                          onError={e => { e.target.style.display = 'none'; }} />
                      ) : (
                        <Users size={44} color="#c4b5fd" style={{ opacity: 0.4 }} />
                      )}
                      <span style={{ position: 'absolute', top: 10, left: 10, background: band.available !== false ? 'rgba(16,185,129,0.88)' : 'rgba(239,68,68,0.8)', color: '#fff', fontSize: '0.68rem', fontFamily: 'Gilroy-semiBold, sans-serif', borderRadius: 6, padding: '3px 9px', backdropFilter: 'blur(6px)' }}>
                        {band.available !== false ? 'Available' : 'Unavailable'}
                      </span>
                      {userLocation?.latitude && band.distance && (
                        <span style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(15,23,42,0.75)', color: '#e2e8f0', fontSize: '0.68rem', fontFamily: 'Gilroy-semiBold', borderRadius: 6, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 3, backdropFilter: 'blur(6px)' }}>
                          <MapPin size={10} />{band.distance < 1 ? '<1km' : `${Math.round(band.distance)}km`}
                        </span>
                      )}
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%', background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)' }} />
                    </div>

                    {/* Info */}
                    <div style={{ padding: '14px 14px 16px' }}>
                      <div style={{ fontFamily: 'Gilroy-semiBold', fontWeight: 700, color: '#fff', fontSize: '0.98rem', lineHeight: 1.3, marginBottom: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {band.name}
                      </div>
                      {band.rating > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 5 }}>
                          {Array.from({ length: fullStars }).map((_, i) => <Star key={i} size={12} fill="#fbbf24" color="#fbbf24" />)}
                          {hasHalf && <StarHalf size={12} fill="#fbbf24" color="#fbbf24" />}
                          <span style={{ color: '#6b7280', fontSize: '0.72rem', fontFamily: 'Poppins, sans-serif', marginLeft: 2 }}>{Number(band.rating).toFixed(1)}</span>
                        </div>
                      )}
                      {tagsLine && (
                        <div style={{ color: '#94a3b8', fontSize: '0.75rem', fontFamily: 'Poppins, sans-serif', marginBottom: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tagsLine}
                        </div>
                      )}
                      {(band.city || band.location) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#6b7280', fontSize: '0.73rem', fontFamily: 'Poppins, sans-serif', marginBottom: band.pricing ? 6 : 0 }}>
                          <MapPin size={11} style={{ flexShrink: 0 }} />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{band.city || band.location}</span>
                        </div>
                      )}
                      {band.pricing && (
                        <div style={{ color: '#10b981', fontFamily: 'Gilroy-semiBold', fontSize: '0.82rem', fontWeight: 700 }}>
                          ₹{band.pricing}/{band.pricingUnit || 'session'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button onClick={scrollRight}
              className={`absolute right-3 top-1/2 -translate-y-1/2 z-20 rounded-full w-9 h-9 flex items-center justify-center transition-all duration-300 ${showScrollButtons ? 'opacity-100' : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'}`}
              style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer' }}>
              <ChevronRight size={18} />
            </button>
          </div>

          {/* See all bands — centered below carousel */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32, paddingBottom: 24 }}>
            <button onClick={() => navigate('/all-bands')}
              style={{ background: 'none', border: 'none', color: '#818cf8', fontFamily: 'Gilroy-semiBold', fontSize: 'clamp(0.9rem, 3vw, 1rem)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', padding: '8px 16px', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; e.currentTarget.style.color = '#a5b4fc'; }}
              onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; e.currentTarget.style.color = '#818cf8'; }}>
              See all bands <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* ── Location Dialog ── */}
      {showLocationDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #0F0F23 100%)', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'Gilroy-semiBold', color: '#e2e8f0', fontSize: '1.15rem', margin: 0 }}>Find bands near you</h3>
              <button onClick={() => { setShowLocationDialog(false); setManualCity(''); }} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <X size={22} />
              </button>
            </div>
            <p style={{ color: '#94a3b8', fontFamily: 'Poppins, sans-serif', fontSize: '0.85rem', marginBottom: 16 }}>
              Enter your city to discover local bands.
            </p>
            <input type="text" value={manualCity}
              onChange={e => setManualCity(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleManualLocation()}
              placeholder="e.g. Mumbai, Delhi, Bangalore"
              autoFocus
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, background: 'rgba(255,255,255,0.06)', color: '#fff', fontFamily: 'Poppins, sans-serif', fontSize: '0.95rem', outline: 'none', marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowLocationDialog(false)}
                style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: '#9ca3af', borderRadius: 8, padding: '8px 18px', fontFamily: 'Gilroy-semiBold', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleManualLocation} disabled={manualCity.trim().length < 2}
                style={{ background: manualCity.trim().length < 2 ? 'rgba(55,78,169,0.3)' : '#374EA9', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 18px', fontFamily: 'Gilroy-semiBold', cursor: manualCity.trim().length < 2 ? 'not-allowed' : 'pointer', transition: 'background 0.2s' }}>
                Find Bands
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scroll-to-top */}
      {showArrow && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 flex items-center justify-center rounded-full w-12 h-12 bg-blue-500 text-white shadow-lg hover:shadow-xl hover:scale-110 active:scale-95 transition-all duration-200 z-50 border-2 border-white/20 backdrop-blur-sm">
          <ArrowUp size={20} />
        </button>
      )}
    </section>
  );
};

export default FeaturedBands;
